import type { Booking } from 'repositories/BerthApi/types/booking'
import type {
  BookingMasterData,
  TenantParameter,
} from 'repositories/BerthApi/getBookingMasterData/type'

import { formatTime, timeToMinutes } from '../../utils/timeConvert'

export const getPackageTypeName = (
  packageTypeId: string | null,
  masterData: BookingMasterData | null
): string => {
  if (!packageTypeId || !masterData?.packageTypes) return ''
  const found = masterData.packageTypes.find((p) => String(p.parameterId) === packageTypeId)
  return found?.parameterName ?? ''
}

export const ADDITIONAL_COLUMN_PATTERN = /^(import|export)AdditionalColumn\d+$/

export const getAdditionalFields = (
  params: TenantParameter[],
  booking: Booking
): { label: string; value: string }[] => {
  return params
    .filter((p) => p.displayFlag)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map((p) => {
      const camelKey = p.columnName.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
      if (!ADDITIONAL_COLUMN_PATTERN.test(camelKey)) return null
      const val = booking[camelKey as keyof Booking]
      return val != null && String(val) !== '' ? { label: p.displayName, value: String(val) } : null
    })
    .filter((f): f is NonNullable<typeof f> => f !== null)
}

/** 荷降ろし / 荷積みの全項目を displayOrder 順で返す（標準項目 + 追加項目） */
export const getSortedImportExportFields = (
  params: TenantParameter[],
  booking: Booking,
  masterData: BookingMasterData | null
): { label: string; value: string }[] => {
  return params
    .filter((p) => p.displayFlag)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .map((p) => {
      const camelKey = p.columnName.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())

      // 荷姿は ID → 名称に変換
      if (camelKey === 'importPackageType' || camelKey === 'exportPackageType') {
        const name = getPackageTypeName(
          booking[camelKey as keyof Booking] as string | null,
          masterData
        )
        return name ? { label: p.displayName, value: name } : null
      }

      const val = booking[camelKey as keyof Booking]
      return val != null && String(val) !== '' ? { label: p.displayName, value: String(val) } : null
    })
    .filter((f): f is NonNullable<typeof f> => f !== null)
}

export const formatTimeRange = (booking: Booking): string => {
  const startRaw = booking.startTime ?? booking.planStartTime
  const endRaw = booking.endTime ?? booking.planEndTime
  const start = startRaw ? formatTime(timeToMinutes(startRaw)) : '--:--'
  const end = endRaw ? formatTime(timeToMinutes(endRaw)) : '--:--'
  return `${start} - ${end}`
}

export const calcDurationMinutes = (booking: Booking): number | null => {
  const startRaw = booking.startTime ?? booking.planStartTime
  const endRaw = booking.endTime ?? booking.planEndTime
  if (!startRaw || !endRaw) return null
  const diff = timeToMinutes(endRaw) - timeToMinutes(startRaw)
  if (diff > 0) return diff
  // 日を跨ぐ予約（例: 22:00 - 02:00）は翌日分の1440分を足す
  if (diff < 0) return diff + 24 * 60
  return null
}
